import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Skeleton,
  Avatar,
  Container,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Badge,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory"
import PersonIcon from "@mui/icons-material/Person"
import NotificationsIcon from "@mui/icons-material/Notifications"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import RefreshIcon from "@mui/icons-material/Refresh"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import CategoryIcon from "@mui/icons-material/Category"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import FilterListIcon from "@mui/icons-material/FilterList"
import { getInvoices, getDashboardAnalytics, getEnhancedDeliveryPreference } from "../../../utils/api"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts"

const StyledCard = styled(Card)(({ theme, gradient }) => ({
  borderRadius: 20,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
  background: gradient || theme.palette.background.paper,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  border: `1px solid ${theme.palette.divider}`,
  height: "100%",
  minHeight: 180,
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.16)",
    "& .stat-icon": {
      transform: "scale(1.1)",
    },
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: 16,
    minHeight: 160,
    "&:hover": {
      transform: "translateY(-3px)",
    },
  },
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    borderRadius: 16,
  },
}))

const GradientAvatar = styled(Avatar)(({ theme }) => ({
  background: theme.palette.action.selected,
  color: theme.palette.text.primary,
  transition: 'all 0.2s ease',
}));

const MetricCard = styled(StyledCard)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: theme.palette.divider,
  },
}))

const QuickActionCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.15)",
  },
  [theme.breakpoints.down("sm")]: {
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
}))

const CHART_COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f", "#00796b", "#f57c00", "#7b1fa2"]

function StatCard({ icon: Icon, label, value, color, trend, loading = false, subtitle }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  return (
    <MetricCard
      sx={{
        height: "100%",
        minHeight: { xs: 180, sm: 200, md: 220 },
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        <Stack spacing={2} height="100%" justifyContent="space-between">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <GradientAvatar
              className="stat-icon"
              color={color}
              sx={{
                width: { xs: 40, sm: 48, md: 52, lg: 56 },
                height: { xs: 40, sm: 48, md: 52, lg: 56 },
              }}
            >
              <Icon sx={{ fontSize: { xs: 20, sm: 24, md: 26, lg: 28 } }} />
            </GradientAvatar>
            {trend && !isMobile && (
              <Chip
                icon={<TrendingUpIcon />}
                label={trend}
                size="small"
                color={trend.startsWith("+") ? "success" : trend.startsWith("-") ? "error" : "default"}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: { xs: 11, sm: 12 } }}
              />
            )}
          </Stack>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{
                fontSize: { xs: 11, sm: 12, md: 13 },
                textTransform: "uppercase",
                letterSpacing: 0.8,
                mb: 1,
              }}
            >
              {label}
            </Typography>
            {loading ? (
              <Skeleton width={120} height={32} />
            ) : (
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  fontSize: { xs: 18, sm: 22, md: 24, lg: 26 },
                  background: `linear-gradient(135deg, ${color}, ${color}80)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: subtitle ? 0.5 : 0,
                  lineHeight: 1.2,
                }}
              >
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10, sm: 11, md: 12 } }}>
                {subtitle}
              </Typography>
            )}
            {trend && isMobile && (
              <Typography
                variant="caption"
                color={trend.startsWith("+") ? "success.main" : trend.startsWith("-") ? "error.main" : "text.secondary"}
                sx={{ fontSize: { xs: 10, sm: 11, md: 12 }, fontWeight: 600 }}
              >
                {trend}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </MetricCard>
  )
}

function RecentInvoiceItem({ invoice }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  return (
    <ListItem sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
          <ShoppingCartIcon sx={{ fontSize: 16 }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: 13, sm: 14 } }}>
            {invoice.customerNumber}
          </Typography>
        }
        secondary={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="success.main" fontWeight={600}>
              ₹{Number(invoice.amount)?.toLocaleString() || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {invoice.date}
            </Typography>
          </Stack>
        }
      />
      <Chip
        label={invoice.category}
        size="small"
        color="secondary"
        variant="outlined"
        sx={{ fontSize: { xs: 9, sm: 10 } }}
      />
    </ListItem>
  )
}

function RevenueChart({ data, selectedPeriod }) {
  const chartData = [
    { name: "Today", value: data.today || 0 },
    { name: "Last 7 Days", value: data.last7Days || 0 },
    { name: "This Month", value: data.thisMonth || 0 },
    { name: "Last Month", value: data.lastMonth || 0 },
  ]

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <RechartsTooltip formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]} />
        <Area type="monotone" dataKey="value" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
      </AreaChart>
    </ResponsiveContainer>
  )
}

function MetricsComparisonChart({ analytics, selectedPeriod }) {
  const chartData = [
    {
      name: "Revenue",
      current: analytics.totalRevenue?.[selectedPeriod] || 0,
      previous:
        selectedPeriod === "today"
          ? analytics.totalRevenue?.last7Days || 0
          : selectedPeriod === "last7Days"
            ? analytics.totalRevenue?.lastMonth || 0
            : analytics.totalRevenue?.lastMonth || 0,
    },
    {
      name: "Invoices",
      current: analytics.totalInvoices?.[selectedPeriod] || 0,
      previous:
        selectedPeriod === "today"
          ? analytics.totalInvoices?.last7Days || 0
          : selectedPeriod === "last7Days"
            ? analytics.totalInvoices?.lastMonth || 0
            : analytics.totalInvoices?.lastMonth || 0,
    },
    {
      name: "Customers",
      current: analytics.uniqueCustomers?.[selectedPeriod] || 0,
      previous:
        selectedPeriod === "today"
          ? analytics.uniqueCustomers?.last7Days || 0
          : selectedPeriod === "last7Days"
            ? analytics.uniqueCustomers?.lastMonth || 0
            : analytics.uniqueCustomers?.lastMonth || 0,
    },
  ]

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <RechartsTooltip />
        <Legend />
        <Bar dataKey="current" fill="#1976d2" name="Current Period" />
        <Bar dataKey="previous" fill="#ed6c02" name="Previous Period" />
      </BarChart>
    </ResponsiveContainer>
  )
}

function CategoryChart({ categoryData }) {
  const data =
    categoryData?.map((item, index) => ({
      name: item.category,
      value: item.revenue,
      color: CHART_COLORS[index % CHART_COLORS.length],
    })) || []

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <RechartsTooltip formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]} />
      </PieChart>
    </ResponsiveContainer>
  )
}

export default function BusinessDashboard() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const [invoices, setInvoices] = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [shop, setShop] = useState(null)
  const [deliverySettings, setDeliverySettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [activeTab, setActiveTab] = useState(0)
  const [selectedPeriod, setSelectedPeriod] = useState("today")
  const [selectedDate, setSelectedDate] = useState("")
  const [analyticsData, setAnalyticsData] = useState({
    totalInvoices: {
      last7Days: 0,
      today: 0,
      thisMonth: 0,
      lastMonth: 0,
      trend: "0%",
    },
    repeatedCustomers: {
      allTime: 0,
      last7Days: 0,
      today: 0,
      thisMonth: 0,
      lastMonth: 0,
      trend: "0%",
    },
    totalRevenue: {
      allTime: 0,
      last7Days: 0,
      today: 0,
      thisMonth: 0,
      lastMonth: 0,
      trend: "0%",
    },
    uniqueCustomers: {
      allTime: 0,
      last7Days: 0,
      today: 0,
      thisMonth: 0,
      lastMonth: 0,
      trend: "0%",
    },
    activeShops: {
      allTime: 0,
      last7Days: 0,
      today: 0,
      thisMonth: 0,
      lastMonth: 0,
      trend: "0%",
    },
    avgInvoiceValue: {
      allTime: 0,
      last7Days: 0,
      today: 0,
    },
    invoicesPerCustomer: {
      allTime: 0,
      last7Days: 0,
      today: 0,
    },
    invoicesPerShop: {
      allTime: 0,
      last7Days: 0,
      today: 0,
    },
    newCustomers: {
      allTime: 0,
      last7Days: 0,
      today: 0,
      thisMonth: 0,
      lastMonth: 0,
      trend: "0%",
    },
    revenueByShop: [],
    categoryWisePurchases: [],
  })

  const loadData = async () => {
    const shopId = localStorage.getItem("shopId")
    if (!shopId) {
      setError("No shop ID found. Please log in again.")
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const queryParams = selectedPeriod === 'custom'
        ? (selectedDate ? { start: selectedDate } : {})
        : { timePeriod: selectedPeriod.toLowerCase() };

      const [invRes, analyticsRes, deliveryRes] = await Promise.all([
        getInvoices(),
        getDashboardAnalytics(queryParams),
        getEnhancedDeliveryPreference(shopId),
      ])

      // Get analytics data
      const analytics = analyticsRes.data
      const allInvoices = ((invRes.data && invRes.data.results) || []).map((inv) => ({
        ...inv,
        shopId: inv.shopId || inv.company_id,
      }))
      // Find shop data from revenue by shop
      const shopData = analytics.revenueByShop?.[0] || {}
      setShop({ name: shopData.shop, revenue: shopData.revenue })

      // Set analytics data for stats
      setAnalytics(analytics)
      setInvoices(allInvoices.filter((inv) => inv.shopId === shopId))
      setDeliverySettings(deliveryRes.data)
    } catch {
      setError("Failed to load data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // Refetch when selectedPeriod or selectedDate changes
  }, [selectedPeriod, selectedDate])

  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod)
    if (newPeriod !== "custom") {
      setSelectedDate("")
    }
  }

  const getStatsForPeriod = () => {
    if (!analytics) {
      return {
        totalSales: 0,
        totalInvoices: 0,
        uniqueCustomers: 0,
        newCustomers: 0,
        avgInvoiceValue: 0,
        salesTrend: "+0%",
        invoicesTrend: "+0%",
        customersTrend: "+0%",
      }
    }

    const pickMetric = (metric) => {
      const obj = analytics?.[metric] || {}
      if (typeof obj?.value !== 'undefined') {
        return { value: obj.value || 0, trend: obj.trend || '+0%' }
      }
      const periodKey = selectedPeriod === 'custom' ? 'selectedDate' : selectedPeriod
      const value = obj?.[periodKey] || 0
      const trend = obj?.trend || obj?.trends?.[periodKey] || '+0%'
      return { value, trend }
    }

    const totalRevenue = pickMetric('totalRevenue')
    const totalInvoices = pickMetric('totalInvoices')
    const uniqueCustomers = pickMetric('uniqueCustomers')
    const repeatedCustomersMetric = pickMetric('repeatedCustomers')

    const avgObj = analytics?.avgInvoiceValue || {}
    const avgPeriodKey = selectedPeriod === 'custom' ? 'selectedDate' : selectedPeriod
    const avgInvoice = typeof avgObj.value !== 'undefined' ? (avgObj.value || 0) : (avgObj?.[avgPeriodKey] || 0)

    return {
      totalSales: totalRevenue.value,
      totalInvoices: totalInvoices.value,
      uniqueCustomers: uniqueCustomers.value,
      repeatedCustomers: repeatedCustomersMetric.value,
      avgInvoiceValue: avgInvoice,
      salesTrend: totalRevenue.trend,
      invoicesTrend: totalInvoices.trend,
      customersTrend: uniqueCustomers.trend,
      repeatedTrend: repeatedCustomersMetric.trend,
    }
  }

  const stats = getStatsForPeriod()

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <StyledPaper sx={{ p: { xs: 3, sm: 4, md: 5 }, mb: { xs: 3, sm: 4, md: 5 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 3, sm: 4 }}
          >
            <Stack direction="row" alignItems="center" spacing={{ xs: 2, sm: 3, md: 4 }}>
              {shop ? (
                <>
                  <GradientAvatar
                    color="#1976d2"
                    sx={{
                      width: { xs: 48, sm: 56, md: 72 },
                      height: { xs: 48, sm: 56, md: 72 },
                    }}
                  >
                    <StoreMallDirectoryIcon sx={{ fontSize: { xs: 24, sm: 28, md: 36 } }} />
                  </GradientAvatar>
                  <Box>
                    <Typography 
                      variant="h3" 
                      fontWeight={800} 
                      color="text.primary" 
                      sx={{ fontSize: { xs: 20, sm: 24, md: 32 } }}
                    >
                      Welcome back, {shop.name}!
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      mt={1.5}
                    >
                      <Chip
                        icon={<LocationOnIcon />}
                        label={shop.location}
                        color="primary"
                        variant="outlined"
                        size="medium"
                        sx={{ fontSize: { xs: 11, sm: 12, md: 14 } }}
                      />
                      <Chip
                        icon={<CategoryIcon />}
                        label={shop.category}
                        color="secondary"
                        variant="outlined"
                        size="medium"
                        sx={{ fontSize: { xs: 11, sm: 12, md: 14 } }}
                      />
                    </Stack>
                  </Box>
                </>
              ) : (
                <Stack direction="row" spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                  <Skeleton variant="circular" width={48} height={48} />
                  <Box>
                    <Skeleton variant="text" width={180} height={24} />
                    <Skeleton variant="text" width={120} height={20} />
                  </Box>
                </Stack>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <Tooltip title="Refresh Data">
                <IconButton
                  onClick={loadData}
                  disabled={loading}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                    width: { xs: 40, sm: 44, md: 48 },
                    height: { xs: 40, sm: 44, md: 48 },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Notifications">
                <Badge badgeContent={3} color="error">
                  <IconButton
                    sx={{
                      bgcolor: "background.paper",
                      width: { xs: 40, sm: 44, md: 48 },
                      height: { xs: 40, sm: 44, md: 48 },
                    }}
                  >
                    <NotificationsIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                  </IconButton>
                </Badge>
              </Tooltip>
            </Stack>
          </Stack>
        </StyledPaper>

        <StyledPaper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: { xs: 2, sm: 3, md: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <FilterListIcon color="primary" sx={{ fontSize: 20 }} />
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }}
              >
                Analytics Filter
              </Typography>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
              <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 140 } }}>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  disabled={loading}
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    fontSize: { xs: 12, sm: 14 },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      py: 1,
                    },
                  }}
                >
                  <MenuItem value="today">📅 Today</MenuItem>
                  <MenuItem value="last7Days">📊 Last 7 Days</MenuItem>
                  <MenuItem value="thisMonth">📈 This Month</MenuItem>
                  <MenuItem value="custom">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <TextField
                        type="date"
                        value={selectedDate}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelectedDate(e.target.value);
                          setSelectedPeriod("custom");
                        }}
                        disabled={loading}
                        size="small"
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: 11, sm: 12 },
                            py: 0.5,
                            px: 1,
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: 'none',
                            },
                          },
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </StyledPaper>

        <Grid container spacing={{ xs: 3, sm: 4, md: 5 }}>
          <Grid item xs={12}>
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} mb={{ xs: 3, sm: 4, md: 5 }}>
              {[
                {
                  icon: AttachMoneyIcon,
                  label: "Total Revenue",
                  value: `₹${stats.totalSales.toLocaleString()}`,
                  color: "#1976d2",
                  trend: stats.salesTrend,
                  subtitle:
                    selectedPeriod === "today"
                      ? "Today's Revenue"
                      : selectedPeriod === "last7Days"
                        ? "Last 7 Days Revenue"
                        : selectedPeriod === "thisMonth"
                          ? "This Month Revenue"
                          : selectedPeriod === "custom" && selectedDate
                            ? `Revenue for ${selectedDate}`
                            : "Revenue",
                },
                {
                  icon: ShoppingCartIcon,
                  label: "Total Invoices",
                  value: (stats.totalInvoices || 0).toString(),
                  color: "#2e7d32",
                  trend: stats.invoicesTrend,
                  subtitle:
                    selectedPeriod === "today"
                      ? "Today's Invoices"
                      : selectedPeriod === "last7Days"
                        ? "Last 7 Days Invoices"
                        : selectedPeriod === "thisMonth"
                          ? "This Month Invoices"
                          : selectedPeriod === "custom" && selectedDate
                            ? `Invoices for ${selectedDate}`
                            : "Invoices",
                },
                {
                  icon: PersonIcon,
                  label: "Customers",
                  value: (stats.uniqueCustomers || 0).toString(),
                  color: "#ed6c02",
                  trend: stats.customersTrend,
                  subtitle:
                    selectedPeriod === "today"
                      ? "Today's Customers"
                      : selectedPeriod === "last7Days"
                        ? "Last 7 Days Customers"
                        : selectedPeriod === "thisMonth"
                          ? "This Month Customers"
                          : selectedPeriod === "custom" && selectedDate
                            ? `Customers for ${selectedDate}`
                            : "Customers",
                },
                {
                  icon: PersonAddIcon,
                  label: "Repeated Customers",
                  value: (stats.repeatedCustomers || 0).toString(),
                  color: "#9c27b0",
                  trend: stats.repeatedTrend,
                  subtitle:
                    selectedPeriod === "today"
                      ? "Today's Repeated"
                      : selectedPeriod === "last7Days"
                        ? "Last 7 Days Repeated"
                        : selectedPeriod === "thisMonth"
                          ? "This Month Repeated"
                          : selectedPeriod === "custom" && selectedDate
                            ? `Repeated for ${selectedDate}`
                            : "Repeated Customers",
                },
              ].map((stat, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  lg={3}
                  key={stat.label}
                  sx={{
                    display: "flex",
                  }}
                >
                  <StatCard {...stat} loading={loading} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}
